import { useEffect, useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Check, X, Star, Users, Car, MessageSquare, BadgeCheck, BookOpen, Search, Plus, Pencil, EyeOff, Eye } from "lucide-react";
import { toast } from "sonner";
import api, { formatPrice } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import UserDetailModal from "../components/UserDetailModal";
import ListingEditorModal from "../components/ListingEditorModal";

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("pending");
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const [allListings, setAllListings] = useState([]);
  const [users, setUsers] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [editorListingId, setEditorListingId] = useState(null); // null=closed, ""=create, id=edit
  const [listingSearch, setListingSearch] = useState("");
  const [listingStatusFilter, setListingStatusFilter] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [userRoleFilter, setUserRoleFilter] = useState("");

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) navigate("/login?next=/admin");
  }, [user, loading, navigate]);

  const refresh = async () => {
    if (!user || user.role !== "admin") return;
    const [s, p, a, u, d] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/listings", { params: { status: "pending", limit: 100, include_suspended: true } }),
      api.get("/listings", { params: { status: "", limit: 200, include_suspended: true } }),
      api.get("/admin/users"),
      api.get("/dealers"),
    ]);
    setStats(s.data);
    setPending(p.data.items || []);
    setAllListings(a.data.items || []);
    setUsers(u.data || []);
    setDealers(d.data || []);
  };

  useEffect(() => { refresh(); }, [user]);

  const moderate = async (id, status) => {
    await api.patch(`/admin/listings/${id}/status`, { status });
    toast.success(`Listing ${status}`);
    refresh();
  };

  const toggleFeatured = async (id, featured) => {
    await api.patch(`/admin/listings/${id}/status`, { featured });
    toast.success(featured ? "Marked featured" : "Unfeatured");
    refresh();
  };

  const toggleSuspended = async (id, suspended) => {
    await api.patch(`/admin/listings/${id}/status`, { suspended });
    toast.success(suspended ? "Listing suspended" : "Listing un-suspended");
    refresh();
  };

  const verifyDealer = async (id, verified) => {
    await api.patch(`/admin/dealers/${id}/verify?verified=${verified}`);
    toast.success(verified ? "Dealer verified" : "Verification removed");
    refresh();
  };

  if (!user || user.role !== "admin") return null;

  const tabs = [
    { id: "pending", label: "Pending Approval", icon: Check, count: pending.length },
    { id: "listings", label: "All Listings", icon: Car, count: allListings.length },
    { id: "users", label: "Users", icon: Users, count: users.length },
    { id: "dealers", label: "Dealers", icon: BadgeCheck, count: dealers.length },
  ];

  return (
    <div data-testid="admin-dashboard-page" className="bg-slate-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10">
        <div>
          <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-cm-primary">Admin Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Marketplace oversight and moderation</p>
        </div>

        {/* Quick links */}
        <div className="mt-4 flex flex-wrap gap-2">
          <Link to="/admin/blog" data-testid="admin-blog-link" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-cm-primary hover:bg-cm-accent text-white text-sm font-medium press-scale">
            <BookOpen className="w-4 h-4" /> Manage blog
          </Link>
        </div>

        {/* Stats */}
        {stats && (
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { k: "Users", v: stats.total_users, icon: Users },
              { k: "Dealers", v: stats.total_dealers, icon: BadgeCheck },
              { k: "Listings", v: stats.total_listings, icon: Car },
              { k: "Pending", v: stats.pending_listings, icon: Check },
              { k: "Approved", v: stats.approved_listings, icon: Check },
              { k: "Enquiries", v: stats.total_enquiries, icon: MessageSquare },
              { k: "Rental requests", v: stats.total_rental_requests, icon: MessageSquare },
            ].map((s) => (
              <div key={s.k} className="bg-white border border-slate-200 rounded-2xl p-4">
                <div className="flex items-center gap-2 text-cm-accent"><s.icon className="w-4 h-4" /></div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-2">{s.k}</div>
                <div className="font-display font-extrabold text-2xl text-cm-primary mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="mt-6 flex gap-2 overflow-x-auto no-scrollbar">
          {tabs.map((t) => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`px-4 py-2.5 rounded-xl flex items-center gap-2 text-sm font-medium whitespace-nowrap transition-colors ${tab === t.id ? 'bg-cm-primary text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'}`}>
              <t.icon className="w-4 h-4" /> {t.label} <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${tab === t.id ? 'bg-white/20' : 'bg-slate-100'}`}>{t.count}</span>
            </button>
          ))}
        </div>

        <div className="mt-6">
          {tab === "pending" && (
            <div data-testid="admin-pending" className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
              {pending.length === 0 ? (
                <div className="p-10 text-center text-slate-500">All clear. No pending listings.</div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {pending.map((l) => (
                    <div key={l.id} className="flex items-center gap-4 p-4">
                      <Link to={`/listing/${l.id}`} className="w-20 h-14 rounded-lg bg-slate-100 overflow-hidden flex-shrink-0">
                        {l.images?.[0] && <img src={l.images[0]} alt="" className="w-full h-full object-cover" />}
                      </Link>
                      <div className="flex-1 min-w-0">
                        <Link to={`/listing/${l.id}`} className="font-semibold text-cm-primary truncate hover:underline">{l.year} {l.brand} {l.model}</Link>
                        <div className="text-xs text-slate-500 mt-0.5">{l.location} · {l.listing_type === 'rental' ? `$${l.daily_price}/day` : formatPrice(l.price)} · by {l.dealer?.dealer_name || l.seller?.full_name}</div>
                      </div>
                      <div className="flex gap-2">
                        <button data-testid={`approve-${l.id}`} onClick={() => moderate(l.id, "approved")} className="px-3 py-2 rounded-lg bg-emerald-500 text-white text-sm font-medium flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Approve</button>
                        <button data-testid={`reject-${l.id}`} onClick={() => moderate(l.id, "rejected")} className="px-3 py-2 rounded-lg bg-red-500 text-white text-sm font-medium flex items-center gap-1"><X className="w-3.5 h-3.5" /> Reject</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "listings" && (
            <div data-testid="admin-all-listings">
              <div className="bg-white border border-slate-200 rounded-2xl p-3 mb-3 flex flex-wrap items-center gap-2">
                <div className="flex-1 min-w-[200px] flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
                  <Search className="w-4 h-4 text-slate-400" />
                  <input data-testid="listing-search" value={listingSearch} onChange={(e) => setListingSearch(e.target.value)} placeholder="Search by brand, model or location…" className="flex-1 bg-transparent outline-none text-sm" />
                </div>
                <select data-testid="listing-status-filter" value={listingStatusFilter} onChange={(e) => setListingStatusFilter(e.target.value)} className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm">
                  <option value="">All statuses</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="sold">Sold</option>
                  <option value="rented">Rented</option>
                </select>
                <button onClick={() => setEditorListingId("")} data-testid="admin-new-listing-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-cm-accent hover:bg-cm-primary text-white text-sm font-semibold press-scale">
                  <Plus className="w-4 h-4" /> New listing
                </button>
              </div>
              <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                <div className="divide-y divide-slate-100">
                  {allListings
                    .filter((l) => !listingStatusFilter || l.status === listingStatusFilter)
                    .filter((l) => {
                      if (!listingSearch) return true;
                      const s = listingSearch.toLowerCase();
                      return (l.brand?.toLowerCase().includes(s) || l.model?.toLowerCase().includes(s) || l.location?.toLowerCase().includes(s));
                    })
                    .map((l) => (
                    <div key={l.id} className="flex items-center gap-3 p-4">
                      <Link to={`/listing/${l.id}`} className="w-16 h-12 rounded-lg bg-slate-100 overflow-hidden flex-shrink-0">
                        {l.images?.[0] && <img src={l.images[0]} alt="" className="w-full h-full object-cover" />}
                      </Link>
                      <div className="flex-1 min-w-0">
                        <Link to={`/listing/${l.id}`} className="font-semibold text-cm-primary truncate hover:underline text-sm">{l.year} {l.brand} {l.model}</Link>
                        <div className="text-xs text-slate-500 mt-0.5 truncate">{l.location} · {l.listing_type} · {l.listing_type === 'rental' ? `$${l.daily_price || 0}/day` : formatPrice(l.price)}</div>
                      </div>
                      <span className={`hidden sm:inline-flex text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full ${l.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : l.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{l.status}</span>
                      {l.suspended && <span className="hidden sm:inline-flex text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full bg-red-100 text-red-700">suspended</span>}
                      <button data-testid={`feature-${l.id}`} onClick={() => toggleFeatured(l.id, !l.featured)} title={l.featured ? "Unfeature" : "Feature"} className={`w-8 h-8 rounded-lg flex items-center justify-center ${l.featured ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                        <Star className={`w-4 h-4 ${l.featured ? 'fill-amber-500 text-amber-500' : ''}`} />
                      </button>
                      <button data-testid={`suspend-${l.id}`} onClick={() => toggleSuspended(l.id, !l.suspended)} title={l.suspended ? "Un-suspend" : "Suspend"} className={`w-8 h-8 rounded-lg flex items-center justify-center ${l.suspended ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                        {l.suspended ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                      <button data-testid={`edit-listing-${l.id}`} onClick={() => setEditorListingId(l.id)} title="Edit" className="w-8 h-8 rounded-lg bg-cm-primary hover:bg-cm-accent text-white flex items-center justify-center">
                        <Pencil className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {allListings.length === 0 && (
                    <div className="p-10 text-center text-slate-500 text-sm">No listings yet.</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {tab === "users" && (
            <div data-testid="admin-users">
              <div className="bg-white border border-slate-200 rounded-2xl p-3 mb-3 flex flex-wrap items-center gap-2">
                <div className="flex-1 min-w-[200px] flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
                  <Search className="w-4 h-4 text-slate-400" />
                  <input data-testid="user-search" value={userSearch} onChange={(e) => setUserSearch(e.target.value)} placeholder="Search by name, email or phone…" className="flex-1 bg-transparent outline-none text-sm" />
                </div>
                <select data-testid="user-role-filter" value={userRoleFilter} onChange={(e) => setUserRoleFilter(e.target.value)} className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm">
                  <option value="">All roles</option>
                  <option value="user">Users</option>
                  <option value="dealer">Dealers</option>
                  <option value="admin">Admins</option>
                </select>
              </div>
              <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                <div className="divide-y divide-slate-100">
                  {users
                    .filter((u) => !userRoleFilter || u.role === userRoleFilter)
                    .filter((u) => {
                      if (!userSearch) return true;
                      const s = userSearch.toLowerCase();
                      return (u.full_name?.toLowerCase().includes(s) ||
                        u.email?.toLowerCase().includes(s) ||
                        u.phone?.toLowerCase().includes(s));
                    })
                    .map((u) => {
                      const status = u.status || "active";
                      const statusBadge = status === "active" ? "bg-emerald-100 text-emerald-700" :
                        status === "suspended" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700";
                      return (
                        <button key={u.id} onClick={() => setSelectedUserId(u.id)} data-testid={`user-row-${u.id}`} className="w-full text-left flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors">
                          <div className="w-10 h-10 rounded-full bg-cm-primary text-white flex items-center justify-center font-semibold flex-shrink-0">{u.full_name?.[0]?.toUpperCase()}</div>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-cm-primary truncate">{u.full_name}</div>
                            <div className="text-xs text-slate-500 truncate">{u.email} · {u.phone || '—'}</div>
                          </div>
                          <span className={`hidden sm:inline-flex text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full ${statusBadge}`}>{status}</span>
                          <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full ${u.role === 'admin' ? 'bg-purple-100 text-purple-700' : u.role === 'dealer' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'}`}>{u.role}</span>
                        </button>
                      );
                    })}
                </div>
              </div>
            </div>
          )}

          {tab === "dealers" && (
            <div data-testid="admin-dealers" className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
              <div className="divide-y divide-slate-100">
                {dealers.map((d) => (
                  <div key={d.id} className="flex items-center gap-4 p-4">
                    <div className="w-12 h-12 rounded-xl bg-slate-100 overflow-hidden flex-shrink-0">{d.logo ? <img src={d.logo} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center font-bold text-cm-primary">{d.dealer_name?.[0]}</div>}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <Link to={`/dealer/${d.id}`} className="font-semibold text-cm-primary truncate hover:underline">{d.dealer_name}</Link>
                        {d.verified_status && <BadgeCheck className="w-4 h-4 text-emerald-500" />}
                      </div>
                      <div className="text-xs text-slate-500 truncate">{d.address}</div>
                    </div>
                    {d.user_id && (
                      <button onClick={() => setSelectedUserId(d.user_id)} data-testid={`dealer-manage-${d.id}`} className="hidden md:inline-flex px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-cm-primary text-xs font-medium">
                        Manage owner
                      </button>
                    )}
                    <button data-testid={`verify-${d.id}`} onClick={() => verifyDealer(d.id, !d.verified_status)} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${d.verified_status ? 'bg-slate-100 text-slate-600' : 'bg-emerald-500 text-white'}`}>
                      {d.verified_status ? "Unverify" : "Verify"}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {selectedUserId && (
        <UserDetailModal
          userId={selectedUserId}
          onClose={() => setSelectedUserId(null)}
          onChanged={refresh}
        />
      )}

      {editorListingId !== null && (
        <ListingEditorModal
          listingId={editorListingId || null}
          onClose={() => setEditorListingId(null)}
          onChanged={refresh}
        />
      )}
    </div>
  );
}
